/**
 * Phonemic Awareness screener (4K phonological awareness) — item bank,
 * scripts, and bands. Display name is 'Phonemic Awareness' (v3.67.0,
 * matching the district form's title); ids unchanged.
 *
 * ONE assessment, five ORDERED steps, administered 1:1 in a single sitting.
 * Three parallel forms (A = September, B = January, C = May): same step,
 * same count, different words, matched on phoneme class, syllable shape,
 * and word familiarity so a May score is comparable to a September score.
 *
 * All 75 items and all teacher scripts were authored for Yilmaz Education
 * LLC. Provenance and the per-step construction rules are documented in
 * docs/assessments/ITEM_PROVENANCE.md.
 *
 * Two hard rules about this file:
 *   1. Items are NOT teacher-overridable. Unlike the benchmark screeners
 *      (profile.screenerSettings), no pa_* type reads a custom word list.
 *      Parallel forms only mean something if every student gets the same
 *      matched items — a substituted list destroys September-to-May
 *      comparability and silently invalidates the band. It is also the
 *      path by which third-party published items could enter the product.
 *   2. Nothing here is generated or inferred at runtime. The bank is a
 *      literal transcription; src/constants/paScreenerItems.test.js fails
 *      the build if a banned word ever appears as a scored stimulus.
 */

// ─── Steps ───────────────────────────────────────────────────────────────
// Step 5 uses its OWN testType (pa_onset_fluency), deliberately NOT the
// retired benchmark `first_sound` id. They assessed the same underlying
// skill but were different instruments: first_sound was a 25-word timed
// fluency measure scored 0–25 with per-item latency; this is a 5-item
// untimed mastery check scored 0–5 against a two-band cut point. One
// testType carrying two incompatible scales would eventually be read with
// the wrong denominator — a 4/5 rendered as 4/25.

export const PA_SCREENER_ID = 'pa_screener_4k';
// Display name (v3.67.0, Kemal): the five-step instrument is titled
// "Phonemic Awareness", matching Elmbrook's own 2026-27 form. (Technically
// steps 1–4 are rhyme/syllable level — phonological, not phonemic — but the
// district-familiar name wins; revisit only on a reviewer-facing surface.)
// Internal identifiers (PA_SCREENER_ID, pa_* testTypes) are deliberately
// unchanged.
export const PA_SCREENER_LABEL = 'Phonemic Awareness';
export const PA_SCREENER_GRADE = '4K';

export const PA_STEP_TYPES = [
  'pa_rhyme_recognition',
  'pa_rhyme_production',
  'pa_blending_syllables',
  'pa_segmenting_syllables',
  'pa_onset_fluency',
];

/** Items per step, and therefore the max score. Never hardcode 5 elsewhere. */
export const PA_ITEMS_PER_STEP = 5;

// ─── Bands ───────────────────────────────────────────────────────────────
// Two bands, not four. Cut points stored as numbers.
export const PA_BAND_DEVELOPING = 'developing';
export const PA_BAND_PROFICIENT = 'proficient';

export const PA_BANDS = [
  { id: PA_BAND_DEVELOPING, label: 'Developing', min: 0, max: 3 },
  { id: PA_BAND_PROFICIENT, label: 'Proficient', min: 4, max: 5 },
];

/** The score at or above which a step counts as Proficient. */
export const PA_PROFICIENT_MIN = 4;

// ─── Windows and forms ───────────────────────────────────────────────────
// The app's shared Academic Calendar speaks in quarters ({year}Q{1-4}) and
// is NOT touched by this feature: screener docs keep stamping `quarter`
// exactly as every other assessment does, and additionally stamp `window`.
//
// Window is DERIVED from the administration date and then shown to the
// teacher, who may change it in one tap before submitting. Assessment
// windows slip in real schools — a January screening genuinely given in
// early February is still the January window, and the teacher is the
// authority on that, not the calendar.

export const PA_WINDOW_SEPTEMBER = 'september';
export const PA_WINDOW_JANUARY = 'january';
export const PA_WINDOW_MAY = 'may';

export const PA_WINDOWS = [
  { id: PA_WINDOW_SEPTEMBER, label: 'September', formId: 'A', optional: true },
  { id: PA_WINDOW_JANUARY, label: 'January', formId: 'B', optional: false },
  { id: PA_WINDOW_MAY, label: 'May', formId: 'C', optional: false },
];

/**
 * Academic-Calendar quarter index (1–4) → window (v3.57.0 round 3).
 *
 * The window is DERIVED from the same date/quarter machinery every other
 * assessment stamps `quarter` from — never from a parallel month table, and
 * never as a teacher-facing primary choice (this is ONE instrument
 * administered at three points in the year, not three sibling tests):
 *   Q1 (fall)        → September, Form A
 *   Q2 (late fall–Jan) → January, Form B
 *   Q3 (late Jan–spring) → January, Form B — a slipped January screening
 *   Q4 (spring)      → May, Form C
 * A date inside no quarter window (e.g. August between school years) falls
 * back to the active year's Q1 exactly as AssessmentForm files it →
 * September. The teacher window override survives for real-school slippage,
 * demoted to a small "change window" affordance.
 */
export const PA_WINDOW_BY_QUARTER = {
  1: PA_WINDOW_SEPTEMBER,
  2: PA_WINDOW_JANUARY,
  3: PA_WINDOW_JANUARY,
  4: PA_WINDOW_MAY,
};

export const PA_FORM_BY_WINDOW = {
  [PA_WINDOW_SEPTEMBER]: 'A',
  [PA_WINDOW_JANUARY]: 'B',
  [PA_WINDOW_MAY]: 'C',
};

// ─── Teacher scripts (identical across all three forms) ──────────────────
// Verbatim. The admin note on every step carries the cover-your-mouth
// reminder: this is an AUDITORY assessment and a child who lipreads (or
// reads a word off the screen) has invalidated the item.

export const PA_STEP_DEFS = [
  {
    step: 1,
    testType: 'pa_rhyme_recognition',
    label: 'Rhyme Recognition',
    responseMode: 'yesno', // child answers yes / no
    script: {
      model: 'Rhyming words end the same way. Listen: cold, gold. Cold and gold rhyme because they both end with /old/.',
      practice: {
        prompt: 'Your turn. Do these words rhyme — boot, root?',
        correct: "That's right, boot and root rhyme.",
        incorrect: 'Listen again: boot, root. They rhyme because they both end with /oot/.',
      },
      prompt: "I'll say two words. Tell me yes if they rhyme, or no if they don't.",
      adminNote: 'Keep your mouth covered or turned away so the child hears the words rather than reading your lips.',
    },
  },
  {
    step: 2,
    testType: 'pa_rhyme_production',
    label: 'Rhyme Production',
    responseMode: 'freeRhyme', // child supplies a rhyming word
    script: {
      model: 'Rhyming words end the same way. Listen: hand, sand. Sand rhymes with hand because they both end with /and/.',
      practice: {
        prompt: 'Your turn. Can you tell me a word that rhymes with coat?',
        correct: 'Yes — coat and ____ rhyme.',
        incorrect: "Coat and ____ don't rhyme. Boat rhymes with coat because they both end with /oat/.",
      },
      prompt: "I'll say a word. Say it back to me, then tell me a word that rhymes with it.",
      adminNote: 'Made-up words count as correct. If the child repeats the word itself or only the ending, prompt once: "Can you think of a different word?" Keep your mouth covered.',
    },
  },
  {
    step: 3,
    testType: 'pa_blending_syllables',
    label: 'Blending Syllables',
    responseMode: 'wholeWord', // child says the whole word
    script: {
      model: "I'll say a word in parts, then put it together. Listen: /side – walk/ … sidewalk.",
      practice: {
        prompt: "Your turn. Listen: /pop – corn/. What's the whole word?",
        correct: 'Yes — /pop – corn/ is popcorn.',
        incorrect: 'When I put /pop – corn/ together, it\'s popcorn. Say it with me: popcorn.',
      },
      prompt: "I'll say a word in parts. You tell me the whole word.",
      adminNote: 'A chopping hand motion for each part is fine. Keep your mouth covered.',
    },
  },
  {
    step: 4,
    testType: 'pa_segmenting_syllables',
    label: 'Segmenting Syllables',
    responseMode: 'parts', // child breaks the word apart
    script: {
      model: "I'll say a word and break it into parts. Listen: hotdog … /hot – dog/. Hotdog has two parts.",
      practice: {
        prompt: 'Your turn. Say seven. Now break it into parts.',
        correct: 'Yes — seven breaks into /sev – en/.',
        incorrect: 'Seven has two parts: /sev – en/. Say it with me: /sev – en/.',
      },
      prompt: "I'll say a word. Repeat it, then break it into parts.",
      adminNote: 'The child may use a chopping motion. Keep your mouth covered.',
    },
  },
  {
    step: 5,
    testType: 'pa_onset_fluency',
    // Display label: the container is named "Phonemic Awareness" (v3.67.0),
    // so step 5 keeps the district form's own subtitle for the skill. The
    // testType stays pa_onset_fluency — display string only.
    label: 'Initial Sound Isolation',
    responseMode: 'phoneme', // child gives the first sound
    script: {
      model: "I'll say a word and tell you its very first sound. The word is boat. The first sound in boat is /b/. Say it with me: boat, /b/.",
      practice: {
        prompt: "Your turn. What's the first sound in leg?",
        correct: 'Yes — /l/ is the first sound in leg.',
        incorrect: 'Listen: /l/ … leg. /l/ is the first sound in leg.',
      },
      prompt: "I'll say a word. Repeat it, then tell me the first sound you hear.",
      adminNote: 'Say the sound, never the letter name. Keep your mouth covered — this step is the one children most often pass by lipreading.',
    },
  },
];

// ─── The item bank ───────────────────────────────────────────────────────
// Verbatim from the authored bank. Do not substitute, reorder, or
// "improve" any word. Step 1 items are PAIRS; every other step is a single
// stimulus word.

const FORM_A_ITEMS = {
  // 3 rhyming pairs, 2 non-rhyming. Both non-rhyming pairs deliberately
  // share their first sound, so a child matching beginning sounds instead
  // of endings is caught rather than rewarded.
  pa_rhyme_recognition: [
    { pair: ['sun', 'run'], answer: 'yes' },
    { pair: ['cup', 'pup'], answer: 'yes' },
    { pair: ['milk', 'mop'], answer: 'no' },
    { pair: ['sock', 'rock'], answer: 'yes' },
    { pair: ['book', 'bear'], answer: 'no' },
  ],
  pa_rhyme_production: [
    { word: 'bed' }, { word: 'hen' }, { word: 'top' }, { word: 'ring' }, { word: 'hill' },
  ],
  pa_blending_syllables: [
    { parts: ['rab', 'bit'], word: 'rabbit' },
    { parts: ['pen', 'cil'], word: 'pencil' },
    { parts: ['sun', 'set'], word: 'sunset' },
    { parts: ['muf', 'fin'], word: 'muffin' },
    { parts: ['gar', 'den'], word: 'garden' },
  ],
  pa_segmenting_syllables: [
    { word: 'summer', parts: ['sum', 'mer'] },
    { word: 'rocket', parts: ['rock', 'et'] },
    { word: 'apple', parts: ['ap', 'ple'] },
    { word: 'tennis', parts: ['ten', 'nis'] },
    { word: 'donut', parts: ['dō', 'nut'] },
  ],
  // Single consonant onsets only — no blends, no digraphs. Each form
  // samples the same phoneme classes: two stops, two fricatives, one nasal.
  pa_onset_fluency: [
    { word: 'gum', sound: '/g/' },
    { word: 'sit', sound: '/s/' },
    { word: 'ten', sound: '/t/' },
    { word: 'fan', sound: '/f/' },
    { word: 'nap', sound: '/n/' },
  ],
};

const FORM_B_ITEMS = {
  pa_rhyme_recognition: [
    { pair: ['tree', 'truck'], answer: 'no' },
    { pair: ['bell', 'well'], answer: 'yes' },
    { pair: ['dish', 'fish'], answer: 'yes' },
    { pair: ['hand', 'horse'], answer: 'no' },
    { pair: ['hop', 'top'], answer: 'yes' },
  ],
  pa_rhyme_production: [
    { word: 'dog' }, { word: 'man' }, { word: 'car' }, { word: 'wall' }, { word: 'look' },
  ],
  pa_blending_syllables: [
    { parts: ['mon', 'key'], word: 'monkey' },
    { parts: ['bas', 'ket'], word: 'basket' },
    { parts: ['tī', 'ger'], word: 'tiger' },
    { parts: ['pump', 'kin'], word: 'pumpkin' },
    { parts: ['rō', 'bot'], word: 'robot' },
  ],
  pa_segmenting_syllables: [
    { word: 'kitten', parts: ['kit', 'ten'] },
    { word: 'yellow', parts: ['yel', 'low'] },
    { word: 'picnic', parts: ['pic', 'nic'] },
    { word: 'turtle', parts: ['tur', 'tle'] },
    { word: 'paper', parts: ['pā', 'per'] },
  ],
  pa_onset_fluency: [
    { word: 'duck', sound: '/d/' },
    { word: 'soap', sound: '/s/' },
    { word: 'tub', sound: '/t/' },
    { word: 'fox', sound: '/f/' },
    { word: 'mud', sound: '/m/' },
  ],
};

const FORM_C_ITEMS = {
  pa_rhyme_recognition: [
    { pair: ['rain', 'train'], answer: 'yes' },
    { pair: ['cake', 'cup'], answer: 'no' },
    { pair: ['wing', 'sing'], answer: 'yes' },
    { pair: ['jump', 'bump'], answer: 'yes' },
    { pair: ['leaf', 'lamp'], answer: 'no' },
  ],
  pa_rhyme_production: [
    { word: 'jet' }, { word: 'pin' }, { word: 'rock' }, { word: 'name' }, { word: 'mail' },
  ],
  pa_blending_syllables: [
    { parts: ['pil', 'low'], word: 'pillow' },
    { parts: ['car', 'rot'], word: 'carrot' },
    { parts: ['zip', 'per'], word: 'zipper' },
    { parts: ['back', 'pack'], word: 'backpack' },
    { parts: ['lem', 'on'], word: 'lemon' },
  ],
  pa_segmenting_syllables: [
    { word: 'puppy', parts: ['pup', 'py'] },
    { word: 'sandbox', parts: ['sand', 'box'] },
    { word: 'button', parts: ['but', 'ton'] },
    { word: 'magnet', parts: ['mag', 'net'] },
    { word: 'tiny', parts: ['tī', 'ny'] },
  ],
  pa_onset_fluency: [
    { word: 'pan', sound: '/p/' },
    { word: 'soup', sound: '/s/' },
    { word: 'kid', sound: '/k/' },
    { word: 'hat', sound: '/h/' },
    { word: 'moon', sound: '/m/' },
  ],
};

export const PA_FORMS = {
  A: { id: 'A', window: PA_WINDOW_SEPTEMBER, label: 'Form A — September', items: FORM_A_ITEMS },
  B: { id: 'B', window: PA_WINDOW_JANUARY, label: 'Form B — January', items: FORM_B_ITEMS },
  C: { id: 'C', window: PA_WINDOW_MAY, label: 'Form C — May', items: FORM_C_ITEMS },
};

export const PA_FORM_IDS = ['A', 'B', 'C'];
